/**
 * `SheetViewConfig` — the per-sheet view state a `SheetRenderCoordinator`
 * coordinates off of (time scale, column count, timezone type).
 *
 * It lives in tento/tento-lona-js/sheets so the core `SheetRenderCoordinator`
 * can consume it without reaching back up to the SDK layer; the SDK's modules
 * import it from `@tento/lona/sheets` (and `SheetViewConfigRaw` from
 * `@tento/lona/sheets/platform`).
 */

import type { SheetsNetwork } from "../wire/rows-network.ts";
import type { Timescale } from "../render/timescale.ts";

/**
 * How a day-timescale sheet places its columns in time: in the viewer's
 * timezone, or pinned to a fixed one. Only meaningful when `timescale === "day"`.
 *
 * - `"default"` — render timestamps in the user's timezone.
 * - `"time-traveler"` — lock the sheet's column origin to a fixed
 *   timezone so "noon" columns line up regardless of where the
 *   viewer is. Used by flight / itinerary sheets.
 */
export type RendererTz = "default" | "time-traveler";

/**
 * Raw (flat) form — what gets serialized to preferences or URL.
 * `resolve` narrows into the tagged-union `SheetViewConfig` below
 * so non-day timescales don't carry a stale `tzType` field.
 */
export interface SheetViewConfigRaw {
  /** The stored key (`timeScale`); `resolve` maps it to `SheetViewConfig.timescale`. */
  timeScale: SheetsNetwork.TimeScale;
  count: number;
  dayTzType: RendererTz;
  /** The stored column count per time scale (empty when none is stored). */
  counts: Partial<Record<Timescale, number>>;
}

/** Values that go with `SheetViewConfigRaw`: `resolve`. */
export namespace SheetViewConfigRaw {
  export function resolve(raw: SheetViewConfigRaw): SheetViewConfig {
    if (raw.timeScale === "day") {
      return {
        timescale: "day",
        count: raw.count,
        tzType: raw.dayTzType,
      };
    }
    return {
      timescale: raw.timeScale,
      count: raw.count,
    };
  }
}

/**
 * Narrowed view config — the shape consumers read. Day variant
 * carries `tzType`; week/month/year variants don't. Matches
 * lona-js's tagged-union exactly.
 */
export type SheetViewConfig =
  | {
      timescale: "day";
      count: number;
      tzType: RendererTz;
    }
  | {
      timescale: "year" | "month" | "week";
      count: number;
    };

/** Values that go with `SheetViewConfig`: `label`. */
export namespace SheetViewConfig {
  export function label(config: SheetViewConfig): string {
    const word =
      config.timescale.charAt(0).toUpperCase() + config.timescale.slice(1);
    return config.count > 1 ? word + "s" : word;
  }
}
